import Vue from "vue";

/**
 * Duplicate check before enrolment. Every POST that registers an enrollee (register / registerProvider with
 * type "client") or adds a dependant (addDependant) first asks the API for likely duplicates in the user's
 * agency (POST enrollees/duplicate-check). When there are any, DuplicateCheckModal (mounted in App.vue)
 * asks whether to continue. Cancelling rejects the request like a 409 from the server, so the form's own
 * error handling shows the message and stops its spinner. If the check itself fails, enrolment goes ahead.
 */
export const duplicateState = Vue.observable({ open: false, matches: [], person: null, resolve: null });

const ENROLLEE_URL = /\/api\/v1\/(auth\/)?(register|registerProvider)\/?$/;
const DEPENDANT_URL = /\/api\/v1\/(auth\/)?addDependant\/?$/;

function field(data, key) {
  if (!data) return "";
  if (typeof FormData !== "undefined" && data instanceof FormData) return data.get(key) || "";
  if (typeof data === "string") {
    try { return (JSON.parse(data) || {})[key] || ""; } catch (e) { return ""; }
  }
  return data[key] || "";
}

function askUser(matches, person) {
  return new Promise((resolve) => {
    Object.assign(duplicateState, { open: true, matches, person, resolve });
  });
}

export function installDuplicateGuard(axios) {
  axios.interceptors.request.use(async (config) => {
    if (config.skipDuplicateCheck || String(config.method).toLowerCase() !== "post") return config;
    const url = config.url || "";
    const isEnrollee = ENROLLEE_URL.test(url) && String(field(config.data, "type")).toLowerCase() === "client";
    const isDependant = DEPENDANT_URL.test(url);
    if (!isEnrollee && !isDependant) return config;
    if (!localStorage.getItem("jwt")) return config; // public sign-up: the check needs a signed-in user

    const person = {
      firstname: field(config.data, "firstname"),
      lastname: field(config.data, "lastname"),
      dob: field(config.data, "dob"),
      phone_number: field(config.data, "phone_number"),
      nimc_number: field(config.data, "nimc_number"),
    };
    if (!person.nimc_number && !person.phone_number && !(person.firstname && person.lastname)) return config;

    let matches = [];
    try {
      const res = await axios.post("/api/v1/auth/enrollees/duplicate-check", person, { skipDuplicateCheck: true });
      matches = (res.data && res.data.data) || [];
    } catch (e) {
      return config; // never block enrolment because the check failed
    }
    if (!matches.some((m) => m.level === "likely")) return config;

    const proceed = await askUser(matches, { ...person, kind: isDependant ? "dependant" : "enrollee" });
    if (proceed) return config;

    const message = "Enrolment cancelled: this person may already be enrolled.";
    return Promise.reject({ isDuplicateCancel: true, message, config, response: { status: 409, data: { message, errors: { duplicate: [message] } } } });
  });
}
